import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  draft:{ bg:'#F9F8F5', color:'#8A8A8A', label:'Draft' },
  needs_information:{ bg:'#FFF0E0', color:'#D6720B', label:'Needs Info' },
  ready_for_review:{ bg:'#EDF3FF', color:'#3B5BDB', label:'Ready for Review' },
  approved:{ bg:'#E8F5EB', color:'#2E6B3A', label:'Approved' },
  published:{ bg:'#556B2F22', color:'#556B2F', label:'Published' },
  needs_update:{ bg:'#FFF3DC', color:'#8B5E0A', label:'Needs Update' },
  archived:{ bg:'#F5F5F5', color:'#aaa', label:'Archived' },
};

export default function BrandWebsite() {
  const { user } = useAuth();
  const canApprove = ['super_admin','sales_manager'].includes(user?.role);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPriceRequest, setShowPriceRequest] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [priceForm, setPriceForm] = useState({ requested_price:'', reason:'' });
  const [form, setForm] = useState({
    product_name:'', listing_title:'', description:'',
    seo_title:'', meta_description:'', category:'',
    specifications:'', status:'draft', published_url:'', notes:''
  });

  useEffect(()=>{ load(); },[]);

  const load = () => {
    setLoading(true);
    get('/bc/website-listings').then(d=>setListings(d.data||[])).finally(()=>setLoading(false));
  };

  const openForm = (item=null) => {
    if (item) {
      setEditing(item);
      setForm({
        product_name:item.product_name||'', listing_title:item.listing_title||'',
        description:item.description||'', seo_title:item.seo_title||'',
        meta_description:item.meta_description||'', category:item.category||'',
        specifications:item.specifications||'', status:item.status||'draft',
        published_url:item.published_url||'', notes:item.notes||''
      });
    } else {
      setEditing(null);
      setForm({product_name:'',listing_title:'',description:'',
        seo_title:'',meta_description:'',category:'',
        specifications:'',status:'draft',published_url:'',notes:''});
    }
    setShowForm(true);
  };

  const save = async () => {
    if (!form.product_name) { alert('Product name required'); return; }
    setSaving(true);
    try {
      if (editing) await put('/bc/website-listings/'+editing.id, form);
      else await post('/bc/website-listings', form);
      setShowForm(false);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const submitPriceRequest = async () => {
    if (!priceForm.requested_price || !priceForm.reason) {
      alert('Price and reason required'); return;
    }
    setSaving(true);
    try {
      await post('/bc/website-listings/'+editing.id+'/price-request', priceForm);
      setShowPriceRequest(false);
      setPriceForm({requested_price:'',reason:''});
      alert('Price change request submitted for approval');
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',color:'#2C1F16',margin:'0 0 4px'}}>Website Listings</h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>Manage product content for the Bloans website</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={()=>openForm()}>+ New Listing</button>
      </div>

      {loading ? <div className="spinner"/> : listings.length === 0 ? (
        <div className="empty-state"><h3>No listings yet</h3></div>
      ) : (
        <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'#2C1F16'}}>
                {['Product','Title','Category','Status','Last Reviewed',''].map(h=>(
                  <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                    color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listings.map((l,i)=>{
                const st = STATUS_COLORS[l.status]||STATUS_COLORS.draft;
                return (
                  <tr key={l.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#2C1F16'}}>{l.product_name}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{l.listing_title||'—'}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{l.category||'—'}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                      <span style={{fontSize:9,padding:'3px 10px',borderRadius:20,fontWeight:600,background:st.bg,color:st.color}}>{st.label}</span>
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>
                      {l.last_reviewed ? new Date(l.last_reviewed).toLocaleDateString('en-NG',{day:'numeric',month:'short'}) : '—'}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                      <button className="btn btn-ghost btn-sm" onClick={()=>openForm(l)}>Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:680,width:'92vw'}}>
            <div className="modal-hdr">
              <span className="modal-title">{editing?'Edit Listing':'New Listing'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'80vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Product Name *</label>
                  <input className="form-control" value={form.product_name}
                    onChange={e=>setForm({...form,product_name:e.target.value})} />
                </div>
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Listing Title</label>
                  <input className="form-control" value={form.listing_title}
                    onChange={e=>setForm({...form,listing_title:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input className="form-control" value={form.category}
                    onChange={e=>setForm({...form,category:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={4} value={form.description}
                  onChange={e=>setForm({...form,description:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Specifications</label>
                <textarea className="form-control" rows={2} value={form.specifications}
                  onChange={e=>setForm({...form,specifications:e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">SEO Title</label>
                  <input className="form-control" value={form.seo_title}
                    onChange={e=>setForm({...form,seo_title:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-control" value={form.status}
                    onChange={e=>setForm({...form,status:e.target.value})}>
                    {Object.entries(STATUS_COLORS).map(([k,v])=>(
                      <option key={k} value={k}
                        disabled={k==='approved'&&!canApprove}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Meta Description</label>
                <textarea className="form-control" rows={2} value={form.meta_description}
                  onChange={e=>setForm({...form,meta_description:e.target.value})} />
              </div>
              {form.status==='published' && (
                <div className="form-group">
                  <label className="form-label">Published URL</label>
                  <input className="form-control" value={form.published_url}
                    onChange={e=>setForm({...form,published_url:e.target.value})} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              {editing && !canApprove && (
                <button className="btn btn-outline btn-sm" style={{color:'#8B5E0A'}}
                  onClick={()=>{ setShowForm(false); setShowPriceRequest(true); }}>
                  Request Price Change
                </button>
              )}
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Saving...':editing?'Save Changes':'Create Listing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPriceRequest && editing && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-hdr">
              <span className="modal-title">Request Price Change — {editing.product_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowPriceRequest(false)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#F9F8F5',borderRadius:8,padding:10,marginBottom:12,fontSize:12}}>
                Current price: <strong>₦{parseFloat(editing.approved_price||0).toLocaleString('en-NG')}</strong>
              </div>
              <div className="form-group">
                <label className="form-label">Requested Price (₦) *</label>
                <input type="number" className="form-control" value={priceForm.requested_price}
                  onChange={e=>setPriceForm({...priceForm,requested_price:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Reason *</label>
                <textarea className="form-control" rows={2} value={priceForm.reason}
                  onChange={e=>setPriceForm({...priceForm,reason:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowPriceRequest(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitPriceRequest} disabled={saving}>
                {saving?'Submitting...':'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
